import { useState } from 'react'
import { Bell, CheckCheck, Inbox, Info, Mail, MailOpen } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  useNotificacionesHistorial,
  useNotificacionesStats,
  useMarcarComoLeida,
  useMarcarTodasLeidas,
} from '@/modules/notificaciones/hooks/useNotificaciones'
import { NotificacionesFilters } from '@/modules/notificaciones/components/NotificacionesFilters'
import { NotificacionItem } from '@/modules/notificaciones/components/NotificacionItem'
import type { FiltrosNotificaciones } from '@/modules/notificaciones/types'

export function NotificacionesPage() {
  const [showInfo, setShowInfo] = useState(false)
  const [filtros, setFiltros] = useState<FiltrosNotificaciones>({
    page: 1,
    page_size: 25,
  })

  const { data: historial, isLoading } = useNotificacionesHistorial(filtros)
  const { data: stats } = useNotificacionesStats()
  const marcarLeida = useMarcarComoLeida()
  const marcarTodas = useMarcarTodasLeidas()

  const handleMarcarLeida = (id: number) => {
    marcarLeida.mutate(id)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notificaciones
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Historial de notificaciones de campañas
            </span>
          )}
        </div>
        {stats && stats.no_leidas > 0 && (
          <Button
            className="bg-black hover:bg-gray-800 text-white"
            onClick={() => marcarTodas.mutate()}
            disabled={marcarTodas.isPending}
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            Marcar todas como leídas
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-semibold text-foreground">Total</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <Inbox className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-semibold text-foreground">No leídas</p>
                  <p className="text-3xl font-bold">{stats.no_leidas}</p>
                </div>
                <Mail className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-semibold text-foreground">Leídas</p>
                  <p className="text-3xl font-bold">{stats.leidas}</p>
                </div>
                <MailOpen className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="pt-6">
          <NotificacionesFilters filtros={filtros} onFiltrosChange={setFiltros} />
        </CardContent>
      </Card>

      {/* Lista de notificaciones */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      ) : historial && historial.results.length > 0 ? (
        <div className="space-y-3">
          {historial.results.map((notificacion) => (
            <NotificacionItem
              key={notificacion.id}
              notificacion={notificacion}
              onMarcarLeida={handleMarcarLeida}
            />
          ))}
        </div>
      ) : (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Bell className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">No hay notificaciones</p>
            <p className="text-sm">Las notificaciones de campañas aparecerán aquí</p>
          </CardContent>
        </Card>
      )}

      {/* Paginación */}
      {historial && historial.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={!filtros.page || filtros.page <= 1}
            onClick={() => setFiltros({ ...filtros, page: (filtros.page || 1) - 1 })}
          >
            Anterior
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Página {filtros.page || 1} de {historial.total_pages}
          </span>
          <Button
            variant="outline"
            disabled={(filtros.page || 1) >= historial.total_pages}
            onClick={() => setFiltros({ ...filtros, page: (filtros.page || 1) + 1 })}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  )
}
